// sales-leads-v1
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PROJECT_TYPES } from "@/components/sales/leadUtils";

const API = `${API_BASE}/public/lead-form`;
const selectCls = "mt-1.5 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm";

/** Public enquiry form (no login). Whatever is sent lands as a New lead in the company's Sales Flow. */
export default function LeadForm() {
  const { token } = useParams();
  const [company, setCompany] = useState(null);
  const [bad, setBad] = useState(false);
  const [f, setF] = useState({ name: "", phone: "", email: "", location: "", project_type: "", budget: "", requirement: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { axios.get(`${API}/${token}`).then(({ data }) => setCompany(data.company)).catch(() => setBad(true)); }, [token]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await axios.post(`${API}/${token}`, { ...f, budget: f.budget === "" ? null : Number(f.budget), phone: f.phone || null, email: f.email || null, location: f.location || null, project_type: f.project_type || null, requirement: f.requirement || null });
      setDone(true);
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      setErr(typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join("; ") : "Could not send. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-secondary/40 px-4 py-10" data-testid="lead-form-page">
      <div className="mx-auto max-w-xl rounded-2xl border border-border bg-card p-6 shadow-sm">
        {bad ? <div className="flex gap-3 text-sm"><AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" /><p>This link is not valid. Please ask for a new one.</p></div>
          : done ? <div className="flex gap-3" role="status"><CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" /><div><p className="font-semibold">Thank you!</p><p className="mt-1 text-sm text-muted-foreground">We have received your enquiry{company ? ` at ${company}` : ""} and will get back to you soon.</p></div></div>
          : (
            <>
              <h1 className="text-xl font-semibold">{company ? `Enquire with ${company}` : "Enquiry"}</h1>
              <p className="mt-1 text-sm text-muted-foreground">Tell us about your project and we will contact you.</p>
              <form onSubmit={submit} className="mt-5 grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2"><Label>Your name</Label><Input required minLength={2} value={f.name} onChange={set("name")} className="mt-1.5" data-testid="lf-name" /></div>
                <div><Label>Phone</Label><Input type="tel" value={f.phone} onChange={set("phone")} className="mt-1.5" data-testid="lf-phone" /></div>
                <div><Label>Email</Label><Input type="email" value={f.email} onChange={set("email")} className="mt-1.5" /></div>
                <div><Label>Location</Label><Input value={f.location} onChange={set("location")} className="mt-1.5" /></div>
                <div><Label>Project type</Label><select className={selectCls} value={f.project_type} onChange={set("project_type")}><option value="">Select</option>{PROJECT_TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
                <div className="sm:col-span-2"><Label>Approximate budget (₹)</Label><Input type="number" min="0" value={f.budget} onChange={set("budget")} className="mt-1.5" /></div>
                <div className="sm:col-span-2"><Label>What do you need?</Label><Textarea rows={4} value={f.requirement} onChange={set("requirement")} className="mt-1.5" /></div>
                <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}><label>Website<input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} /></label></div>
                {err && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 sm:col-span-2" role="alert" data-testid="lf-error">{err}</p>}
                <div className="sm:col-span-2"><Button type="submit" disabled={busy} className="w-full" data-testid="lf-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send enquiry</Button></div>
                <p className="text-xs text-muted-foreground sm:col-span-2">Please give a phone number or an email so we can reach you.</p>
              </form>
            </>
          )}
      </div>
    </div>
  );
}
